import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Search,
  Table,
  Key,
  ArrowRight,
  Loader2,
  Database,
  Folder,
  ChevronRight,
  ChevronDown,
  Layers,
  Sparkles,
  CheckCircle2,
  Eye,
  Hash,
  Activity,
  Clock,
  Box,
  FileCode2,
  Zap,
  Filter,
  RefreshCw,
  Copy,
  Check,
  Binary,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Code2,
  GitCommit,
  ShieldCheck,
  ShieldAlert,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { showError, showSuccess } from '../../../utils/toast';

// 1. Database Stats item from /db_monitor_dashboard/schema/stats
export interface SchemaDatabaseStat {
  database: string;
  tables: number;
  views: number;
  sequences: number;
  functions: number;
  procedures: number;
  triggers: number;
  total: number;
  last_updated?: string;
}

// 2. Column detail from /db_monitor_dashboard/schema/table
export interface TableColumnDetail {
  name: string;
  data_type: string;
  is_primary_key?: boolean;
  is_primary?: boolean;
  nullable?: boolean;
  is_nullable?: boolean;
  ordinal_position?: number;
  default?: any;
}

// 3. Index detail from /db_monitor_dashboard/schema/table
export interface TableIndexDetail {
  name: string;
  columns: string[] | string;
  is_unique?: boolean;
  is_primary?: boolean;
}

// 4. Foreign Key detail from /db_monitor_dashboard/schema/table
export interface TableForeignKeyDetail {
  name: string;
  column: string;
  ref_schema?: string;
  ref_table?: string;
  ref_column?: string;
  foreign_table_schema?: string;
  foreign_table_name?: string;
  foreign_column_name?: string;
}

// 5. Object item from /db_monitor_dashboard/schema
export interface SchemaObjectItem {
  database: string;
  schema: string;
  table: string;
  table_type?: string;
  total_size_mb?: number;
  table_size_mb?: number;
  index_size_mb?: number;
  row_estimate?: number;
  schema_hash?: string;
  collected_at?: string;
}

export interface TableMetaDetail extends SchemaObjectItem {
  agent_id?: number;
  asset_id?: number;
  client_id?: number;
  cluster_id?: string;
  config_id?: string;
  db_type?: string;
  hostname?: string;
  ip?: string;
  os?: string;
  organization_id?: number;
  log_timestamp?: string;
  indexed_at?: string;

  // Tables / Views
  columns?: TableColumnDetail[];
  indexes?: TableIndexDetail[];
  foreign_keys?: TableForeignKeyDetail[];

  // Functions / Procedures
  name?: string;
  language?: string;
  return_type?: string;
  routine_type?: string;
  volatility?: string;
  security_definer?: boolean;
  arguments?: string;
  definition?: string;

  // Sequences
  data_type?: string;
  start_value?: number | string;
  min_value?: number | string;
  max_value?: number | string;
  increment_by?: number | string;
  cache_size?: number | string;
  is_cycled?: boolean;
  owned_by_schema?: string;
  owned_by_table?: string;
  owned_by_column?: string;

  // Triggers
  event?: string;
  timing?: string;
  action_statement?: string;
}

export type ViewSelection =
  | { type: 'all' }
  | { type: 'db'; db: string }
  | { type: 'schema'; db: string; schema: string }
  | { type: 'table'; item: SchemaObjectItem };

interface SchemaViewProps {
  activeCluster: string;
  clientId?: string;
  refreshKey?: number;
}

// Normalize raw table type string
export const normalizeTableType = (rawType?: string): string => {
  if (!rawType) return 'table';
  const t = rawType.toLowerCase().trim();
  if (t === 'm' || t.includes('mat')) return 'materialized view';
  if (t === 'v' || t.includes('view')) return 'view';
  if (t === 's' || t.includes('seq')) return 'sequence';
  if (t.includes('func')) return 'function';
  if (t.includes('proc')) return 'procedure';
  if (t.includes('trig')) return 'trigger';
  if (t === 'f' || t.includes('foreign')) return 'foreign table';
  if (t === 'p' || t.includes('part')) return 'partitioned table';
  if (t === 'r' || t === 't' || t.includes('table') || t === 'base table') return 'table';
  return t;
};

export type TableCategory =
  | 'tables'
  | 'views'
  | 'mat_views'
  | 'sequences'
  | 'functions'
  | 'procedures'
  | 'triggers'
  | 'others';

export const PREFERRED_TYPE_ORDER: TableCategory[] = [
  'tables',
  'views',
  'mat_views',
  'sequences',
  'functions',
  'procedures',
  'triggers',
  'others',
];

// Strict Category Mapper
export const getTableTypeCategory = (rawType?: string): TableCategory => {
  if (!rawType) return 'tables';
  const t = rawType.toLowerCase().trim();
  if (t === 'm' || t.includes('mat')) return 'mat_views';
  if (t === 'v' || t.includes('view')) return 'views';
  if (t === 's' || t.includes('seq')) return 'sequences';
  if (t.includes('func')) return 'functions';
  if (t.includes('proc')) return 'procedures';
  if (t.includes('trig')) return 'triggers';
  if (
    t === 'r' ||
    t === 'p' ||
    t === 'f' ||
    t === 't' ||
    t.includes('table') ||
    t.includes('part') ||
    t.includes('foreign')
  )
    return 'tables';
  return 'others';
};

// Strict Filter Matcher
export const isTypeMatch = (rawType?: string, filter: string = 'all'): boolean => {
  if (!filter || filter === 'all') return true;
  const cat = getTableTypeCategory(rawType);
  return cat === filter;
};

export const getTableTypeBadgeInfo = (rawType?: string) => {
  const cat = getTableTypeCategory(rawType);
  const norm = normalizeTableType(rawType);

  if (cat === 'mat_views') {
    return {
      label: 'Mat. View',
      pluralLabel: 'Mat. Views',
      bg: 'bg-purple-500/10',
      border: 'border-purple-500/30',
      text: 'text-purple-400',
      icon: Layers,
    };
  }

  if (cat === 'views') {
    return {
      label: 'View',
      pluralLabel: 'Views',
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-500/30',
      text: 'text-cyan-400',
      icon: Eye,
    };
  }

  if (cat === 'sequences') {
    return {
      label: 'Sequence',
      pluralLabel: 'Sequences',
      bg: 'bg-amber-500/10',
      border: 'border-amber-500/30',
      text: 'text-amber-400',
      icon: Hash,
    };
  }

  if (cat === 'functions') {
    return {
      label: 'Function',
      pluralLabel: 'Functions',
      bg: 'bg-indigo-500/10',
      border: 'border-indigo-500/30',
      text: 'text-indigo-400',
      icon: FileCode2,
    };
  }

  if (cat === 'procedures') {
    return {
      label: 'Procedure',
      pluralLabel: 'Procedures',
      bg: 'bg-violet-500/10',
      border: 'border-violet-500/30',
      text: 'text-violet-400',
      icon: Zap,
    };
  }

  if (cat === 'triggers') {
    return {
      label: 'Trigger',
      pluralLabel: 'Triggers',
      bg: 'bg-rose-500/10',
      border: 'border-rose-500/30',
      text: 'text-rose-400',
      icon: Activity,
    };
  }

  if (cat === 'tables') {
    if (norm === 'partitioned table') {
      return {
        label: 'Partitioned Table',
        pluralLabel: 'Partitioned Tables',
        bg: 'bg-blue-500/10',
        border: 'border-blue-500/30',
        text: 'text-blue-400',
        icon: Layers,
      };
    }
    if (norm === 'foreign table') {
      return {
        label: 'Foreign Table',
        pluralLabel: 'Foreign Tables',
        bg: 'bg-pink-500/10',
        border: 'border-pink-500/30',
        text: 'text-pink-400',
        icon: Box,
      };
    }
    return {
      label: 'Table',
      pluralLabel: 'Tables',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/30',
      text: 'text-emerald-400',
      icon: Table,
    };
  }

  return {
    label: rawType ? rawType.toUpperCase() : 'Object',
    pluralLabel: 'Others',
    bg: 'bg-gray-500/10',
    border: 'border-gray-500/30',
    text: 'text-gray-300',
    icon: Table,
  };
};

const formatDate = (isoStr?: string) => {
  if (!isoStr) return '-';
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return isoStr;
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return isoStr;
  }
};

export const SchemaView: React.FC<SchemaViewProps> = ({
  activeCluster,
  clientId,
  refreshKey,
}) => {
  // Step 1: Database stats list from /db_monitor_dashboard/schema/stats
  const [loadingStats, setLoadingStats] = useState(false);
  const [dbStats, setDbStats] = useState<SchemaDatabaseStat[]>([]);

  // Step 2: Database schemas and objects cache: dbName -> SchemaObjectItem[]
  const [dbObjectsCache, setDbObjectsCache] = useState<Record<string, SchemaObjectItem[]>>({});
  const [dbLoadingMap, setDbLoadingMap] = useState<Record<string, boolean>>({});
  const inFlightDbFetches = useRef<Set<string>>(new Set());

  // Global search & per-node filters
  const [search, setSearch] = useState('');
  const [schemaSearchMap, setSchemaSearchMap] = useState<Record<string, string>>({});
  const [tableSearchMap, setTableSearchMap] = useState<Record<string, string>>({});
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [dbViewTab, setDbViewTab] = useState<'schemas' | 'objects'>('schemas');
  const [copiedHash, setCopiedHash] = useState(false);
  const [copiedDef, setCopiedDef] = useState(false);

  // Sorting for Objects Tables
  const [sortField, setSortField] = useState<'name' | 'type' | 'rows' | 'index_size' | 'size'>('name');
  const [sortAsc, setSortAsc] = useState<boolean>(true);

  // Navigation & Tree Selection State
  const [activeSelection, setActiveSelection] = useState<ViewSelection>({ type: 'all' });
  const [expandedDbs, setExpandedDbs] = useState<Set<string>>(new Set());
  const [expandedSchemas, setExpandedSchemas] = useState<Set<string>>(new Set());
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());

  // Step 3: Table / Object Detail State from /db_monitor_dashboard/schema/table
  const [tableMeta, setTableMeta] = useState<TableMetaDetail | null>(null);
  const [loadingMeta, setLoadingMeta] = useState(false);

  // 1. Fetch initial database statistics
  const fetchDbStats = useCallback(async () => {
    if (!activeCluster) return;
    setLoadingStats(true);
    try {
      const res = await baseapi.get('/db_monitor_dashboard/schema/stats', {
        params: { cluster_id: activeCluster, client_id: clientId },
      });
      const data = res.data?.data || (Array.isArray(res.data) ? res.data : []);
      setDbStats(data);
    } catch (err: any) {
      console.warn('SchemaView stats fetch error:', err);
      setDbStats([]);
    } finally {
      setLoadingStats(false);
    }
  }, [activeCluster, clientId]);

  useEffect(() => {
    setDbObjectsCache({});
    inFlightDbFetches.current.clear();
    setTableMeta(null);
    setActiveSelection({ type: 'all' });
    setTypeFilter('all');
    fetchDbStats();
  }, [fetchDbStats, refreshKey]);

  // 2. Fetch schema objects for a specific database (with deduplication)
  const fetchDbSchema = useCallback(
    async (dbName: string, force = false) => {
      if (!activeCluster || !dbName) return [];

      // If already cached and not force refreshing, return cache
      if (!force && dbObjectsCache[dbName]) {
        return dbObjectsCache[dbName];
      }

      // If already in-flight, skip duplicate call
      if (inFlightDbFetches.current.has(dbName)) {
        return [];
      }

      inFlightDbFetches.current.add(dbName);
      setDbLoadingMap((prev) => ({ ...prev, [dbName]: true }));
      try {
        const res = await baseapi.get('/db_monitor_dashboard/schema', {
          params: {
            cluster_id: activeCluster,
            client_id: clientId,
            database: dbName,
          },
        });
        const data = res.data?.data || (Array.isArray(res.data) ? res.data : []);
        setDbObjectsCache((prev) => ({ ...prev, [dbName]: data }));
        return data;
      } catch (err: any) {
        console.warn(`Schema fetch error for database ${dbName}:`, err);
        showError(err.message || `Failed to fetch schema for ${dbName}`);
        return [];
      } finally {
        inFlightDbFetches.current.delete(dbName);
        setDbLoadingMap((prev) => ({ ...prev, [dbName]: false }));
      }
    },
    [activeCluster, clientId, dbObjectsCache]
  );

  // Automatically fetch schema when user navigates to db or schema view
  useEffect(() => {
    if (activeSelection.type === 'db' && activeSelection.db) {
      fetchDbSchema(activeSelection.db);
    } else if (activeSelection.type === 'schema' && activeSelection.db) {
      fetchDbSchema(activeSelection.db);
    }
  }, [activeSelection, fetchDbSchema]);

  // 3. Fetch detailed table/function/sequence metadata from /db_monitor_dashboard/schema/table on click
  const fetchTableDetails = useCallback(
    async (item: SchemaObjectItem) => {
      setLoadingMeta(true);
      setTableMeta(null);
      try {
        const res = await baseapi.get('/db_monitor_dashboard/schema/table', {
          params: {
            cluster_id: activeCluster,
            client_id: clientId,
            database: item.database,
            schema: item.schema,
            table: item.table,
          },
        });
        const data = res.data?.data || res.data;
        setTableMeta({
          ...item,
          ...data,
          columns: data?.columns || [],
          indexes: data?.indexes || [],
          foreign_keys: data?.foreign_keys || [],
          database: data?.database || item.database,
          schema: data?.schema || item.schema,
          table: data?.table || item.table,
          name: data?.name || item.table,
          table_type: data?.table_type || item.table_type,
          row_estimate: data?.row_estimate ?? item.row_estimate,
          schema_hash: data?.schema_hash || item.schema_hash,
          total_size_mb: data?.total_size_mb ?? item.total_size_mb,
          index_size_mb: data?.index_size_mb ?? item.index_size_mb,
          table_size_mb: data?.table_size_mb ?? item.table_size_mb,
          collected_at: data?.collected_at || item.collected_at,

          // Function details
          language: data?.language,
          return_type: data?.return_type,
          routine_type: data?.routine_type,
          volatility: data?.volatility,
          security_definer: data?.security_definer,
          arguments: data?.arguments,
          definition: data?.definition,

          // Sequence details
          data_type: data?.data_type,
          start_value: data?.start_value,
          min_value: data?.min_value,
          max_value: data?.max_value,
          increment_by: data?.increment_by,
          cache_size: data?.cache_size,
          is_cycled: data?.is_cycled,
          owned_by_schema: data?.owned_by_schema,
          owned_by_table: data?.owned_by_table,
          owned_by_column: data?.owned_by_column,
        });
      } catch (err: any) {
        console.warn('Table detail fetch error:', err);
        showError(err.message || `Failed to fetch definition for ${item.table}`);
        // Fallback to basic item info
        setTableMeta(item);
      } finally {
        setLoadingMeta(false);
      }
    },
    [activeCluster, clientId]
  );

  // Hierarchical DB -> Schema -> Object Type -> Objects structure
  const dbHierarchy = useMemo(() => {
    const map: Record<
      string,
      {
        totalMb: number;
        schemas: Record<
          string,
          {
            totalMb: number;
            types: Record<string, SchemaObjectItem[]>;
            allObjects: SchemaObjectItem[];
          }
        >;
      }
    > = {};

    Object.keys(dbObjectsCache).forEach((dbName) => {
      const items = dbObjectsCache[dbName] || [];
      if (!map[dbName]) map[dbName] = { totalMb: 0, schemas: {} };

      items.forEach((item) => {
        const sch = item.schema || 'public';
        const rawType = item.table_type || 'table';
        const typeCategory = getTableTypeCategory(rawType);

        if (!map[dbName].schemas[sch]) {
          map[dbName].schemas[sch] = { totalMb: 0, types: {}, allObjects: [] };
        }
        if (!map[dbName].schemas[sch].types[typeCategory]) {
          map[dbName].schemas[sch].types[typeCategory] = [];
        }

        const mb = item.total_size_mb || 0;
        map[dbName].schemas[sch].allObjects.push(item);
        map[dbName].schemas[sch].types[typeCategory].push(item);
        map[dbName].schemas[sch].totalMb += mb;
        map[dbName].totalMb += mb;
      });
    });

    return map;
  }, [dbObjectsCache]);

  // Handle tree interactions
  const handleToggleDb = (dbName: string) => {
    setExpandedDbs((prev) => {
      const next = new Set(prev);
      if (next.has(dbName)) {
        next.delete(dbName);
      } else {
        next.add(dbName);
        fetchDbSchema(dbName);
      }
      return next;
    });
    setActiveSelection({ type: 'db', db: dbName });
  };

  const handleToggleSchema = (dbName: string, schemaName: string) => {
    const key = `${dbName}:${schemaName}`;
    setExpandedSchemas((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setActiveSelection({ type: 'schema', db: dbName, schema: schemaName });
  };

  const handleToggleType = (dbName: string, schemaName: string, typeCat: string) => {
    const key = `${dbName}:${schemaName}:${typeCat}`;
    setExpandedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelectTable = (item: SchemaObjectItem) => {
    const dbName = item.database;
    const schemaName = item.schema;
    const typeCat = getTableTypeCategory(item.table_type);

    setExpandedDbs((prev) => new Set(prev).add(dbName));
    setExpandedSchemas((prev) => new Set(prev).add(`${dbName}:${schemaName}`));
    setExpandedTypes((prev) => new Set(prev).add(`${dbName}:${schemaName}:${typeCat}`));
    setActiveSelection({ type: 'table', item });
    fetchTableDetails(item);
  };

  const selectDbFromView = (dbName: string) => {
    setExpandedDbs((prev) => new Set(prev).add(dbName));
    setActiveSelection({ type: 'db', db: dbName });
  };

  const selectSchemaFromView = (dbName: string, schemaName: string) => {
    setExpandedDbs((prev) => new Set(prev).add(dbName));
    setExpandedSchemas((prev) => new Set(prev).add(`${dbName}:${schemaName}`));
    setActiveSelection({ type: 'schema', db: dbName, schema: schemaName });
  };

  const isTreeActive = (type: string, id: string) => {
    if (activeSelection.type === 'db' && type === 'db') return activeSelection.db === id;
    if (activeSelection.type === 'schema' && type === 'schema')
      return `${activeSelection.db}:${activeSelection.schema}` === id;
    if (activeSelection.type === 'table' && type === 'table')
      return `${activeSelection.item.database}:${activeSelection.item.schema}:${activeSelection.item.table}` === id;
    return false;
  };

  const copyHashToClipboard = (hashStr: string) => {
    navigator.clipboard.writeText(hashStr);
    setCopiedHash(true);
    showSuccess('Schema hash copied to clipboard');
    setTimeout(() => setCopiedHash(false), 2000);
  };

  const copyDefinitionToClipboard = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedDef(true);
    showSuccess('Source definition copied to clipboard');
    setTimeout(() => setCopiedDef(false), 2000);
  };

  const handleSortToggle = (field: 'name' | 'type' | 'index_size' | 'size') => {
    if (sortField === field) {
      setSortAsc((prev) => !prev);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const sortItems = useCallback(
    (items: SchemaObjectItem[]) => {
      return items.slice().sort((a, b) => {
        let cmp = 0;
        if (sortField === 'name') {
          cmp = a.table.localeCompare(b.table);
        } else if (sortField === 'type') {
          const typeA = normalizeTableType(a.table_type);
          const typeB = normalizeTableType(b.table_type);
          cmp = typeA.localeCompare(typeB) || a.table.localeCompare(b.table);
        } else if (sortField === 'index_size') {
          cmp = (a.index_size_mb || 0) - (b.index_size_mb || 0);
        } else if (sortField === 'size') {
          cmp = (a.total_size_mb || 0) - (b.total_size_mb || 0);
        }
        return sortAsc ? cmp : -cmp;
      });
    },
    [sortField, sortAsc]
  );

  const searchLower = search.trim().toLowerCase();

  // List of all databases known (from stats or cache)
  const allDbNames = useMemo(() => {
    const names = new Set<string>();
    dbStats.forEach((s) => names.add(s.database));
    Object.keys(dbObjectsCache).forEach((k) => names.add(k));
    return Array.from(names).sort();
  }, [dbStats, dbObjectsCache]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-2.5 h-[calc(100vh-170px)] relative">
      {/* LEFT: Interactive Tree Panel */}
      <div className="lg:col-span-3 bg-gradient-to-b from-gray-900/90 via-gray-900/70 to-gray-900/90 border border-gray-700/60 rounded-2xl p-3.5 shadow-2xl backdrop-blur-xl flex flex-col overflow-hidden">
        {/* Explore Schema / All Databases Reset Button */}
        <button
          onClick={() => {
            setActiveSelection({ type: 'all' });
            setTypeFilter('all');
          }}
          className={`w-full py-2.5 px-3.5 mb-3 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 border transition-all duration-200 ${
            activeSelection.type === 'all'
              ? 'bg-gradient-to-r from-orange-500 to-amber-600 border-orange-400 text-white shadow-lg shadow-orange-950/40 scale-[1.01]'
              : 'bg-gray-900/80 hover:bg-gray-800/80 border-gray-700/80 text-gray-300 hover:text-white shadow-inner'
          }`}
        >
          <Layers className={`w-4 h-4 ${activeSelection.type === 'all' ? 'text-white' : 'text-orange-400'}`} />
          All Databases Overview
        </button>

        {/* Tree Search Box */}
        <div className="relative mb-3 shrink-0">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search databases..."
            className="w-full bg-gray-950/80 border border-gray-700/70 rounded-xl pl-8 pr-3 py-2 text-xs text-white placeholder-gray-400 focus:outline-none focus:border-orange-500/50 transition-all shadow-inner"
          />
        </div>

        {/* Tree Container */}
        <div className="flex-1 overflow-y-auto custom-scroll space-y-1 pr-1">
          {loadingStats ? (
            <div className="flex flex-col items-center justify-center py-12 text-xs text-gray-400 gap-2">
              <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
              <span className="font-medium">Loading database statistics...</span>
            </div>
          ) : allDbNames.length > 0 ? (
            allDbNames.map((dbName) => {
              const stat = dbStats.find((s) => s.database === dbName);
              const dbData = dbHierarchy[dbName];
              const isLoadingThisDb = dbLoadingMap[dbName];
              const isDbExpanded = expandedDbs.has(dbName);
              const isDbActive = isTreeActive('db', dbName);

              const dbSchFilter = (schemaSearchMap[dbName] || '').trim().toLowerCase();

              // Schema names
              const schemaNames = dbData?.schemas ? Object.keys(dbData.schemas).sort() : [];

              const isDbNameMatch = searchLower ? dbName.toLowerCase().includes(searchLower) : true;

              if (searchLower && !isDbNameMatch) {
                return null;
              }

              return (
                <div key={dbName} className="space-y-1">
                  {/* Database Node */}
                  <div
                    onClick={() => handleToggleDb(dbName)}
                    className={`flex items-center gap-2 px-2.5 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-all ${
                      isDbActive
                        ? 'bg-orange-500/15 text-orange-400 border border-orange-500/40 font-bold shadow-md shadow-orange-950/20'
                        : 'hover:bg-gray-800/60 text-gray-200 hover:text-white border border-transparent'
                    }`}
                  >
                    <span className="text-gray-400 shrink-0">
                      {isLoadingThisDb ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-400" />
                      ) : isDbExpanded ? (
                        <ChevronDown className="w-3.5 h-3.5 text-gray-300" />
                      ) : (
                        <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                      )}
                    </span>
                    <Database className="w-4 h-4 text-orange-500 shrink-0" />
                    <span className="truncate flex-1 font-mono text-[11px]">{dbName}</span>
                    <span className="text-[10px] text-orange-300 bg-gray-900 border border-gray-800 px-1.5 py-0.5 rounded font-mono shrink-0">
                      {stat ? `${stat.total} ` : `${schemaNames.length} sch`}
                    </span>
                  </div>

                  {/* Schemas Container */}
                  {isDbExpanded && (
                    <div className="border-l-2 border-orange-500/20 ml-3.5 my-1 pl-2.5 space-y-1">
                      {/* Search Schemas */}
                      <div className="relative my-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                        <Search className="w-3 h-3 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                          type="text"
                          value={schemaSearchMap[dbName] || ''}
                          onChange={(e) =>
                            setSchemaSearchMap((prev) => ({ ...prev, [dbName]: e.target.value }))
                          }
                          placeholder="Search schemas..."
                          className="w-full bg-gray-950/90 border border-gray-700/60 rounded-lg pl-7 pr-2 py-1 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 transition-all shadow-inner font-mono"
                        />
                      </div>

                      {isLoadingThisDb ? (
                        <div className="flex items-center gap-2 py-2 px-3 text-[11px] text-gray-400">
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-400" />
                          <span>Fetching schemas & tables...</span>
                        </div>
                      ) : schemaNames.length > 0 ? (
                        schemaNames
                          .filter((sn) => {
                            if (dbSchFilter && !sn.toLowerCase().includes(dbSchFilter)) return false;
                            return true;
                          })
                          .map((schemaName) => {
                            const schData = dbData?.schemas?.[schemaName];
                            if (!schData) return null;
                            const schKey = `${dbName}:${schemaName}`;
                            const isSchExpanded =
                              (tableSearchMap[schKey] || '').length > 0
                                ? true
                                : expandedSchemas.has(schKey);
                            const isSchActive = isTreeActive('schema', schKey);

                            const tblFilter = (tableSearchMap[schKey] || '').trim().toLowerCase();

                            // Sorted type categories according to preferred order
                            const existingCategories = Object.keys(schData.types || {});
                            const typeCategories = PREFERRED_TYPE_ORDER.filter((cat) =>
                              existingCategories.includes(cat)
                            );
                            existingCategories.forEach((cat) => {
                              if (!typeCategories.includes(cat as TableCategory)) {
                                typeCategories.push(cat as TableCategory);
                              }
                            });

                            return (
                              <div key={schemaName} className="space-y-1">
                                {/* Schema Header */}
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleSchema(dbName, schemaName);
                                  }}
                                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] font-medium cursor-pointer transition-all ${
                                    isSchActive
                                      ? 'bg-blue-500/15 text-blue-300 border border-blue-500/40 font-bold shadow-md'
                                      : 'hover:bg-gray-800/60 text-gray-300 hover:text-white border border-transparent'
                                  }`}
                                >
                                  <span className="text-gray-400 shrink-0">
                                    {isSchExpanded ? (
                                      <ChevronDown className="w-3 h-3 text-gray-300" />
                                    ) : (
                                      <ChevronRight className="w-3 h-3 text-gray-300" />
                                    )}
                                  </span>
                                  <Folder className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                                  <span className="truncate flex-1 font-mono">{schemaName}</span>
                                  <span className="text-[10px] text-blue-300 bg-gray-900 border border-gray-800 px-1.5 py-0.5 rounded font-mono shrink-0">
                                    {schData.allObjects.length}
                                  </span>
                                </div>

                                {/* Schema Types & Objects Container */}
                                {isSchExpanded && (
                                  <div className="border-l border-blue-500/20 ml-3 pl-2 space-y-1 my-1">
                                    {/* Search Objects */}
                                    <div
                                      className="relative my-1 shrink-0"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <Search className="w-3 h-3 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                                      <input
                                        type="text"
                                        value={tableSearchMap[schKey] || ''}
                                        onChange={(e) =>
                                          setTableSearchMap((prev) => ({
                                            ...prev,
                                            [schKey]: e.target.value,
                                          }))
                                        }
                                        placeholder="Search tables/views..."
                                        className="w-full bg-gray-950/90 border border-gray-700/60 rounded-lg pl-7 pr-2 py-1 text-[11px] text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500/50 transition-all shadow-inner font-mono"
                                      />
                                    </div>

                                    {/* Categorized Type Folders */}
                                    {typeCategories.map((typeCat) => {
                                      const typeItems = (schData.types[typeCat] || []).filter((o) => {
                                        if (tblFilter && !o.table.toLowerCase().includes(tblFilter)) {
                                          return false;
                                        }
                                        return true;
                                      });

                                      if (typeItems.length === 0) return null;

                                      const typeKey = `${dbName}:${schemaName}:${typeCat}`;
                                      const isTypeExpanded =
                                        searchLower || tblFilter ? true : expandedTypes.has(typeKey);
                                      const sampleBadge = getTableTypeBadgeInfo(typeCat);
                                      const TypeIcon = sampleBadge.icon;

                                      return (
                                        <div key={typeCat} className="space-y-0.5">
                                          {/* Type Category Header */}
                                          <div
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleToggleType(dbName, schemaName, typeCat);
                                            }}
                                            className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-gray-800/50 cursor-pointer text-[10px] uppercase font-bold tracking-wider text-gray-400 transition-colors"
                                          >
                                            <span className="text-gray-500">
                                              {isTypeExpanded ? (
                                                <ChevronDown className="w-2.5 h-2.5" />
                                              ) : (
                                                <ChevronRight className="w-2.5 h-2.5" />
                                              )}
                                            </span>
                                            <TypeIcon className={`w-3 h-3 ${sampleBadge.text}`} />
                                            <span>{sampleBadge.pluralLabel || sampleBadge.label}</span>
                                            <span className="ml-auto font-mono text-[9px] px-1 py-0.2 rounded bg-gray-850 text-gray-400">
                                              {typeItems.length}
                                            </span>
                                          </div>

                                          {/* Object Items */}
                                          {isTypeExpanded && (
                                            <div className="border-l border-gray-800 ml-2 pl-2 space-y-0.5">
                                              {typeItems
                                                .sort((a, b) => a.table.localeCompare(b.table))
                                                .map((item) => {
                                                  const tblKey = `${dbName}:${schemaName}:${item.table}`;
                                                  const isTblActive = isTreeActive('table', tblKey);
                                                  const badge = getTableTypeBadgeInfo(item.table_type);
                                                  const ItemIcon = badge.icon;

                                                  return (
                                                    <div
                                                      key={`${item.database}_${item.schema}_${item.table}`}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSelectTable(item);
                                                      }}
                                                      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] font-mono cursor-pointer transition-all ${
                                                        isTblActive
                                                          ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white font-bold shadow-md shadow-orange-950/40 scale-[1.01]'
                                                          : 'hover:bg-gray-800/70 text-gray-300 hover:text-white border border-transparent'
                                                      }`}
                                                    >
                                                      <ItemIcon
                                                        className={`w-3 h-3 shrink-0 ${
                                                          isTblActive ? 'text-white' : badge.text
                                                        }`}
                                                      />
                                                      <span className="truncate flex-1">{item.table}</span>
                                                    </div>
                                                  );
                                                })}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })
                      ) : (
                        <div className="py-2 px-3 text-[11px] text-gray-500 italic">
                          Click to load schemas & objects.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="py-12 text-center text-xs text-gray-500 italic">
              No databases found in cluster.
            </div>
          )}
        </div>
      </div>

      {/* RIGHT: Content View Panel */}
      <div className="lg:col-span-9 bg-gradient-to-b from-gray-900/90 via-gray-900/60 to-gray-900/90 border border-gray-700/60 rounded-2xl p-3.5 overflow-y-auto custom-scroll shadow-2xl backdrop-blur-xl flex flex-col">
        {/* ========================================================================= */}
        {/* VIEW 1: All Databases View (/db_monitor_dashboard/schema/stats) */}
        {/* ========================================================================= */}
        {activeSelection.type === 'all' && (
          <div className="space-y-4">
            {/* Header & Quick Summary Cards */}
            <div className="border-b border-gray-700/60 pb-3 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-orange-500/15 border border-orange-500/30 flex items-center justify-center text-orange-400 shadow-inner shrink-0">
                  <Database className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-white tracking-wide flex items-center gap-2">
                    Cluster Databases Overview
                    <span className="px-2 py-0.5 text-[10px] font-mono rounded bg-orange-500/10 text-orange-400 border border-orange-500/30">
                      {dbStats.length} Databases
                    </span>
                  </h2>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    Live schema metrics and object distribution. Select a database to inspect its schemas and tables.
                  </p>
                </div>
              </div>

              <button
                onClick={() => fetchDbStats()}
                disabled={loadingStats}
                className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-gray-800/80 hover:bg-gray-750 border border-gray-700 text-gray-300 hover:text-white flex items-center gap-1.5 transition-all shadow-sm disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 text-orange-400 ${loadingStats ? 'animate-spin' : ''}`} />
                Refresh Stats
              </button>
            </div>

            {/* Aggregated Quick Metrics Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                  Total Objects
                </span>
                <span className="text-sm font-bold text-white font-mono mt-0.5 block">
                  {dbStats.reduce((acc, s) => acc + (s.total || 0), 0)}
                </span>
              </div>
              <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                  Tables
                </span>
                <span className="text-sm font-bold text-emerald-400 font-mono mt-0.5 block">
                  {dbStats.reduce((acc, s) => acc + (s.tables || 0), 0)}
                </span>
              </div>
              <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                  Views
                </span>
                <span className="text-sm font-bold text-cyan-400 font-mono mt-0.5 block">
                  {dbStats.reduce((acc, s) => acc + (s.views || 0), 0)}
                </span>
              </div>
              <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                  Sequences
                </span>
                <span className="text-sm font-bold text-amber-400 font-mono mt-0.5 block">
                  {dbStats.reduce((acc, s) => acc + (s.sequences || 0), 0)}
                </span>
              </div>
              <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                  Functions
                </span>
                <span className="text-sm font-bold text-indigo-400 font-mono mt-0.5 block">
                  {dbStats.reduce((acc, s) => acc + (s.functions || 0), 0)}
                </span>
              </div>
              <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                  Procedures
                </span>
                <span className="text-sm font-bold text-violet-400 font-mono mt-0.5 block">
                  {dbStats.reduce((acc, s) => acc + (s.procedures || 0), 0)}
                </span>
              </div>
              <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                  Triggers
                </span>
                <span className="text-sm font-bold text-rose-400 font-mono mt-0.5 block">
                  {dbStats.reduce((acc, s) => acc + (s.triggers || 0), 0)}
                </span>
              </div>
            </div>

            {/* Database Stats Table */}
            <div className="bg-gray-900/80 border border-gray-700/60 rounded-xl shadow-xl overflow-hidden">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-gray-700/80 text-[10px] font-bold text-gray-400 uppercase tracking-widest bg-gray-900/90">
                    <th className="py-2.5 px-3">DATABASE NAME</th>
                    <th className="py-2.5 px-3 text-center">TABLES</th>
                    <th className="py-2.5 px-3 text-center">VIEWS</th>
                    <th className="py-2.5 px-3 text-center">SEQUENCES</th>
                    <th className="py-2.5 px-3 text-center">FUNCTIONS</th>
                    <th className="py-2.5 px-3 text-center">TRIGGERS</th>
                    <th className="py-2.5 px-3 text-center">TOTAL OBJECTS</th>
                    <th className="py-2.5 px-3 text-right">LAST UPDATED</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800/60">
                  {dbStats.length > 0 ? (
                    dbStats
                      .filter((s) => {
                        if (!searchLower) return true;
                        return s.database.toLowerCase().includes(searchLower);
                      })
                      .sort((a, b) => b.total - a.total || a.database.localeCompare(b.database))
                      .map((stat) => {
                        return (
                          <tr
                            key={stat.database}
                            onClick={() => selectDbFromView(stat.database)}
                            className="hover:bg-orange-500/[0.06] cursor-pointer transition-colors group"
                          >
                            <td className="py-2.5 px-3 font-semibold text-orange-400 group-hover:text-orange-300 font-mono flex items-center gap-2">
                              <Database className="w-3.5 h-3.5 text-orange-500 shrink-0" />
                              {stat.database}
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              <span className="px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-mono text-[11px] font-bold">
                                {stat.tables || 0}
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              <span className="px-2 py-0.5 rounded-md bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 font-mono text-[11px] font-bold">
                                {stat.views || 0}
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              <span className="px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-400 border border-amber-500/20 font-mono text-[11px] font-bold">
                                {stat.sequences || 0}
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              <span className="px-2 py-0.5 rounded-md bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 font-mono text-[11px] font-bold">
                                {stat.functions || 0}
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              <span className="px-2 py-0.5 rounded-md bg-rose-500/10 text-rose-400 border border-rose-500/20 font-mono text-[11px] font-bold">
                                {stat.triggers || 0}
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              <span className="px-2.5 py-1 rounded-full bg-gray-800 border border-gray-700 text-white font-mono text-[11px] font-extrabold shadow-sm">
                                {stat.total || 0}
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-right text-[11px] text-gray-400 font-mono">
                              {formatDate(stat.last_updated)}
                            </td>
                          </tr>
                        );
                      })
                  ) : (
                    <tr>
                      <td colSpan={8} className="py-12 text-center text-xs text-gray-500 italic">
                        {loadingStats ? 'Loading database statistics...' : 'No databases found.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* VIEW 2: Database View (/db_monitor_dashboard/schema?database=...) */}
        {/* ========================================================================= */}
        {activeSelection.type === 'db' && (
          <div className="space-y-4">
            {/* Breadcrumb Trail */}
            <div className="border-b border-gray-700/60 pb-3 space-y-2">
              <div className="text-[11px] text-gray-400 flex items-center gap-1.5 flex-wrap">
                <button
                  onClick={() => {
                    setActiveSelection({ type: 'all' });
                    setTypeFilter('all');
                  }}
                  className="px-2 py-0.5 rounded bg-gray-800/80 border border-gray-700/60 text-gray-300 hover:text-white hover:border-orange-500/40 transition-all font-semibold"
                >
                  All Databases
                </button>
                <span>/</span>
                <span className="text-white font-semibold font-mono">{activeSelection.db}</span>
              </div>

              <div className="flex items-center justify-between flex-wrap gap-3 pt-1">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-orange-500/15 border border-orange-500/30 flex items-center justify-center text-orange-400 shadow-inner shrink-0">
                    <Database className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-white font-mono tracking-wide flex items-center gap-2">
                      {activeSelection.db}
                    </h2>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      Explore schema namespaces and segregated object categories under database{' '}
                      <span className="text-orange-400 font-mono">{activeSelection.db}</span>.
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => fetchDbSchema(activeSelection.db, true)}
                  disabled={dbLoadingMap[activeSelection.db]}
                  className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-gray-800/80 hover:bg-gray-750 border border-gray-700 text-gray-300 hover:text-white flex items-center gap-1.5 transition-all shadow-sm disabled:opacity-50"
                >
                  <RefreshCw
                    className={`w-3.5 h-3.5 text-orange-400 ${
                      dbLoadingMap[activeSelection.db] ? 'animate-spin' : ''
                    }`}
                  />
                  Refresh Database Schema
                </button>
              </div>
            </div>

            {/* Database Stats Cards */}
            {(() => {
              const stat = dbStats.find((s) => s.database === activeSelection.db);
              const dbData = dbHierarchy[activeSelection.db];
              const schemaCount = dbData?.schemas ? Object.keys(dbData.schemas).length : 0;
              const schemasList = dbData?.schemas ? Object.values(dbData.schemas) : [];

              const totalObjects =
                stat?.total !== undefined
                  ? stat.total
                  : schemasList.reduce((acc, s) => acc + s.allObjects.length, 0);

              const totalTables =
                stat?.tables !== undefined
                  ? stat.tables
                  : schemasList.reduce((acc, s) => acc + (s.types['tables']?.length || 0), 0);

              const totalViews =
                stat?.views !== undefined
                  ? stat.views
                  : schemasList.reduce(
                      (acc, s) =>
                        acc +
                        (s.types['views']?.length || 0) +
                        (s.types['mat_views']?.length || 0),
                      0
                    );

              const totalSequences =
                stat?.sequences !== undefined
                  ? stat.sequences
                  : schemasList.reduce((acc, s) => acc + (s.types['sequences']?.length || 0), 0);

              return (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                  <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                    <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                      Total Objects
                    </span>
                    <span className="text-sm font-bold text-white font-mono mt-0.5 block">
                      {totalObjects}
                    </span>
                  </div>
                  <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                    <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                      Tables
                    </span>
                    <span className="text-sm font-bold text-emerald-400 font-mono mt-0.5 block">
                      {totalTables}
                    </span>
                  </div>
                  <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                    <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                      Views
                    </span>
                    <span className="text-sm font-bold text-cyan-400 font-mono mt-0.5 block">
                      {totalViews}
                    </span>
                  </div>
                  <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                    <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                      Sequences
                    </span>
                    <span className="text-sm font-bold text-amber-400 font-mono mt-0.5 block">
                      {totalSequences}
                    </span>
                  </div>
                  <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                    <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                      Schemas
                    </span>
                    <span className="text-sm font-bold text-indigo-400 font-mono mt-0.5 block">
                      {schemaCount}
                    </span>
                  </div>
                  <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                    <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                      Total Disk Size
                    </span>
                    <span className="text-sm font-bold text-orange-400 font-mono mt-0.5 block">
                      {dbData ? `${dbData.totalMb.toFixed(2)} MB` : '-'}
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* Database View Tabs */}
            <div className="flex items-center gap-2 border-b border-gray-800 pb-2">
              <button
                onClick={() => setDbViewTab('schemas')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                  dbViewTab === 'schemas'
                    ? 'bg-orange-500 text-white shadow-md shadow-orange-950/40'
                    : 'bg-gray-900 text-gray-400 hover:text-white border border-gray-800'
                }`}
              >
                <Folder className="w-3.5 h-3.5" />
                <span>Schemas Breakdown</span>
              </button>
              <button
                onClick={() => setDbViewTab('objects')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                  dbViewTab === 'objects'
                    ? 'bg-orange-500 text-white shadow-md shadow-orange-950/40'
                    : 'bg-gray-900 text-gray-400 hover:text-white border border-gray-800'
                }`}
              >
                <Layers className="w-3.5 h-3.5" />
                <span>All Database Objects</span>
                <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-gray-950/60 text-white">
                  {(dbObjectsCache[activeSelection.db] || []).length}
                </span>
              </button>
            </div>

            {/* Schemas List Table or All Database Objects Table */}
            {dbLoadingMap[activeSelection.db] ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400 space-y-2">
                <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                <span className="text-xs">Fetching schemas & objects...</span>
              </div>
            ) : dbViewTab === 'schemas' ? (
              <div className="space-y-2">
                <div className="bg-gray-900/80 border border-gray-700/60 rounded-xl shadow-xl overflow-hidden">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-gray-700/80 text-[10px] font-bold text-gray-400 uppercase tracking-widest bg-gray-900/90">
                        <th className="py-2.5 px-3">SCHEMA NAME</th>
                        <th className="py-2.5 px-3 text-center">TABLES</th>
                        <th className="py-2.5 px-3 text-center">VIEWS</th>
                        <th className="py-2.5 px-3 text-center">TOTAL OBJECTS</th>
                        <th className="py-2.5 px-3">DISK USAGE</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800/60">
                      {(() => {
                        const dbData = dbHierarchy[activeSelection.db];
                        if (!dbData || !dbData.schemas || Object.keys(dbData.schemas).length === 0) {
                          return (
                            <tr>
                              <td colSpan={5} className="py-8 text-center text-xs text-gray-500 italic">
                                No schemas or objects loaded for this database.
                              </td>
                            </tr>
                          );
                        }

                        const maxMb = Math.max(
                          ...Object.values(dbData.schemas).map((s) => s.totalMb),
                          0.01
                        );

                        return Object.keys(dbData.schemas)
                          .sort()
                          .map((schemaName) => {
                            const s = dbData.schemas[schemaName];
                            const tablesCount = s.types['tables']?.length || 0;
                            const viewsCount =
                              (s.types['views']?.length || 0) + (s.types['mat_views']?.length || 0);
                            const pct = Math.min(100, (s.totalMb / maxMb) * 100);

                            return (
                              <tr
                                key={schemaName}
                                onClick={() => selectSchemaFromView(activeSelection.db, schemaName)}
                                className="hover:bg-orange-500/[0.06] cursor-pointer transition-colors group"
                              >
                                <td className="py-2.5 px-3 font-semibold text-blue-400 group-hover:text-blue-300 font-mono flex items-center gap-2">
                                  <Folder className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                                  {schemaName}
                                </td>
                                <td className="py-2.5 px-3 text-center">
                                  <span className="px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-mono text-[11px] font-bold">
                                    {tablesCount}
                                  </span>
                                </td>
                                <td className="py-2.5 px-3 text-center">
                                  <span className="px-2 py-0.5 rounded-md bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 font-mono text-[11px] font-bold">
                                    {viewsCount}
                                  </span>
                                </td>
                                <td className="py-2.5 px-3 text-center">
                                  <span className="px-2 py-0.5 rounded-full bg-gray-800 border border-gray-700 text-gray-200 font-mono text-[10px]">
                                    {s.allObjects.length}
                                  </span>
                                </td>
                                <td className="py-2.5 px-3">
                                  <div className="flex items-center gap-2.5">
                                    <div className="flex-1 h-2 bg-gray-950 rounded-full overflow-hidden border border-gray-700/80">
                                      <div
                                        className="h-full bg-orange-500 rounded-full shadow-[0_0_8px_rgba(249,115,22,0.6)] transition-all duration-500"
                                        style={{ width: `${Math.max(pct, 2.5).toFixed(1)}%` }}
                                      />
                                    </div>
                                    <span className="text-[10px] font-bold font-mono text-orange-400 bg-gray-900 border border-orange-500/30 px-2 py-0.5 rounded shrink-0 whitespace-nowrap text-right shadow-sm min-w-[68px]">
                                      {s.totalMb.toFixed(2)} MB
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            );
                          });
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              /* All Database Objects Tab with Filter Type Pills */
              <div className="space-y-3">
                {(() => {
                  const dbObjects = dbObjectsCache[activeSelection.db] || [];
                  const tablesCount = dbObjects.filter((o) => getTableTypeCategory(o.table_type) === 'tables').length;
                  const viewsCount = dbObjects.filter((o) => getTableTypeCategory(o.table_type) === 'views').length;
                  const matViewsCount = dbObjects.filter((o) => getTableTypeCategory(o.table_type) === 'mat_views').length;
                  const sequencesCount = dbObjects.filter((o) => getTableTypeCategory(o.table_type) === 'sequences').length;
                  const functionsCount = dbObjects.filter((o) => getTableTypeCategory(o.table_type) === 'functions').length;
                  const proceduresCount = dbObjects.filter((o) => getTableTypeCategory(o.table_type) === 'procedures').length;
                  const triggersCount = dbObjects.filter((o) => getTableTypeCategory(o.table_type) === 'triggers').length;
                  const othersCount = dbObjects.filter((o) => getTableTypeCategory(o.table_type) === 'others').length;

                  const filteredObjects = sortItems(
                    dbObjects.filter((obj) => {
                      if (!isTypeMatch(obj.table_type, typeFilter)) return false;
                      if (searchLower) {
                        return (
                          obj.table.toLowerCase().includes(searchLower) ||
                          obj.schema.toLowerCase().includes(searchLower) ||
                          (obj.table_type && obj.table_type.toLowerCase().includes(searchLower))
                        );
                      }
                      return true;
                    })
                  );

                  const maxMb = Math.max(...filteredObjects.map((o) => o.total_size_mb || 0), 0.01);

                  return (
                    <>
                      {/* Filter Type Pills */}
                      <div className="flex items-center gap-2 flex-wrap pb-1">
                        <span className="text-[10px] font-bold uppercase text-gray-400 tracking-wider flex items-center gap-1">
                          <Filter className="w-3 h-3 text-orange-400" /> Filter Type:
                        </span>

                        <button
                          type="button"
                          onClick={() => setTypeFilter('all')}
                          className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all ${
                            typeFilter === 'all'
                              ? 'bg-orange-500 text-white border-orange-400 shadow-md shadow-orange-950/30'
                              : 'bg-gray-850 hover:bg-gray-800 text-gray-300 border-gray-700'
                          }`}
                        >
                          <span>All</span>
                          <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded border ${typeFilter === 'all' ? 'bg-orange-600 border-orange-400 text-white' : 'bg-gray-900 text-gray-300 border-gray-700'}`}>
                            {dbObjects.length}
                          </span>
                        </button>

                        <button
                          type="button"
                          onClick={() => setTypeFilter('tables')}
                          className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all ${
                            typeFilter === 'tables'
                              ? 'bg-emerald-600 text-white border-emerald-400 shadow-md shadow-emerald-950/30'
                              : 'bg-gray-850 hover:bg-gray-800 text-gray-300 border-gray-700'
                          }`}
                        >
                          <Table className={`w-3 h-3 ${typeFilter === 'tables' ? 'text-white' : 'text-emerald-400'}`} />
                          <span>Tables</span>
                          <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded border ${typeFilter === 'tables' ? 'bg-emerald-700 border-emerald-400 text-white' : 'bg-gray-900 text-emerald-300 border-gray-700'}`}>
                            {tablesCount}
                          </span>
                        </button>

                        <button
                          type="button"
                          onClick={() => setTypeFilter('views')}
                          className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all ${
                            typeFilter === 'views'
                              ? 'bg-cyan-600 text-white border-cyan-400 shadow-md shadow-cyan-950/30'
                              : 'bg-gray-850 hover:bg-gray-800 text-gray-300 border-gray-700'
                          }`}
                        >
                          <Eye className={`w-3 h-3 ${typeFilter === 'views' ? 'text-white' : 'text-cyan-400'}`} />
                          <span>Views</span>
                          <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded border ${typeFilter === 'views' ? 'bg-cyan-700 border-cyan-400 text-white' : 'bg-gray-900 text-cyan-300 border-gray-700'}`}>
                            {viewsCount}
                          </span>
                        </button>

                        {matViewsCount > 0 && (
                          <button
                            type="button"
                            onClick={() => setTypeFilter('mat_views')}
                            className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all ${
                              typeFilter === 'mat_views'
                                ? 'bg-purple-600 text-white border-purple-400 shadow-md shadow-purple-950/30'
                                : 'bg-gray-850 hover:bg-gray-800 text-gray-300 border-gray-700'
                            }`}
                          >
                            <Layers className={`w-3 h-3 ${typeFilter === 'mat_views' ? 'text-white' : 'text-purple-400'}`} />
                            <span>Mat. Views</span>
                            <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded border ${typeFilter === 'mat_views' ? 'bg-purple-700 border-purple-400 text-white' : 'bg-gray-900 text-purple-300 border-gray-700'}`}>
                              {matViewsCount}
                            </span>
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => setTypeFilter('sequences')}
                          className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all ${
                            typeFilter === 'sequences'
                              ? 'bg-amber-600 text-white border-amber-400 shadow-md shadow-amber-950/30'
                              : 'bg-gray-850 hover:bg-gray-800 text-gray-300 border-gray-700'
                          }`}
                        >
                          <Hash className={`w-3 h-3 ${typeFilter === 'sequences' ? 'text-white' : 'text-amber-400'}`} />
                          <span>Sequences</span>
                          <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded border ${typeFilter === 'sequences' ? 'bg-amber-700 border-amber-400 text-white' : 'bg-gray-900 text-amber-300 border-gray-700'}`}>
                            {sequencesCount}
                          </span>
                        </button>

                        {functionsCount > 0 && (
                          <button
                            type="button"
                            onClick={() => setTypeFilter('functions')}
                            className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all ${
                              typeFilter === 'functions'
                                ? 'bg-indigo-600 text-white border-indigo-400 shadow-md shadow-indigo-950/30'
                                : 'bg-gray-850 hover:bg-gray-800 text-gray-300 border-gray-700'
                            }`}
                          >
                            <FileCode2 className={`w-3 h-3 ${typeFilter === 'functions' ? 'text-white' : 'text-indigo-400'}`} />
                            <span>Functions</span>
                            <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded border ${typeFilter === 'functions' ? 'bg-indigo-700 border-indigo-400 text-white' : 'bg-gray-900 text-indigo-300 border-gray-700'}`}>
                              {functionsCount}
                            </span>
                          </button>
                        )}

                        {proceduresCount > 0 && (
                          <button
                            type="button"
                            onClick={() => setTypeFilter('procedures')}
                            className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all ${
                              typeFilter === 'procedures'
                                ? 'bg-violet-600 text-white border-violet-400 shadow-md shadow-violet-950/30'
                                : 'bg-gray-850 hover:bg-gray-800 text-gray-300 border-gray-700'
                            }`}
                          >
                            <Zap className={`w-3 h-3 ${typeFilter === 'procedures' ? 'text-white' : 'text-violet-400'}`} />
                            <span>Procedures</span>
                            <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded border ${typeFilter === 'procedures' ? 'bg-violet-700 border-violet-400 text-white' : 'bg-gray-900 text-violet-300 border-gray-700'}`}>
                              {proceduresCount}
                            </span>
                          </button>
                        )}

                        {triggersCount > 0 && (
                          <button
                            type="button"
                            onClick={() => setTypeFilter('triggers')}
                            className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all ${
                              typeFilter === 'triggers'
                                ? 'bg-rose-600 text-white border-rose-400 shadow-md shadow-rose-950/30'
                                : 'bg-gray-850 hover:bg-gray-800 text-gray-300 border-gray-700'
                            }`}
                          >
                            <Activity className={`w-3 h-3 ${typeFilter === 'triggers' ? 'text-white' : 'text-rose-400'}`} />
                            <span>Triggers</span>
                            <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded border ${typeFilter === 'triggers' ? 'bg-rose-700 border-rose-400 text-white' : 'bg-gray-900 text-rose-300 border-gray-700'}`}>
                              {triggersCount}
                            </span>
                          </button>
                        )}

                        {othersCount > 0 && (
                          <button
                            type="button"
                            onClick={() => setTypeFilter('others')}
                            className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all ${
                              typeFilter === 'others'
                                ? 'bg-gray-600 text-white border-gray-400 shadow-md shadow-gray-950/30'
                                : 'bg-gray-850 hover:bg-gray-800 text-gray-300 border-gray-700'
                            }`}
                          >
                            <Layers className={`w-3 h-3 ${typeFilter === 'others' ? 'text-white' : 'text-gray-400'}`} />
                            <span>Others</span>
                            <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded border ${typeFilter === 'others' ? 'bg-gray-700 border-gray-400 text-white' : 'bg-gray-900 text-gray-300 border-gray-700'}`}>
                              {othersCount}
                            </span>
                          </button>
                        )}
                      </div>

                      {/* Objects Table */}
                      <div className="bg-gray-900/80 border border-gray-700/60 rounded-xl shadow-xl overflow-hidden">
                        <table className="w-full text-left text-xs border-collapse">
                          <thead>
                            <tr className="border-b border-gray-700/80 text-[10px] font-bold text-gray-400 uppercase tracking-widest bg-gray-900/90">
                              <th
                                onClick={() => handleSortToggle('name')}
                                className="py-2.5 px-3 cursor-pointer hover:text-white transition-colors"
                              >
                                <div className="flex items-center gap-1">
                                  <span>OBJECT NAME</span>
                                  {sortField === 'name' ? (
                                    sortAsc ? <ArrowUp className="w-3 h-3 text-orange-400" /> : <ArrowDown className="w-3 h-3 text-orange-400" />
                                  ) : (
                                    <ArrowUpDown className="w-3 h-3 text-gray-500" />
                                  )}
                                </div>
                              </th>
                              <th className="py-2.5 px-3">SCHEMA</th>
                              <th
                                onClick={() => handleSortToggle('type')}
                                className="py-2.5 px-3 cursor-pointer hover:text-white transition-colors"
                              >
                                <div className="flex items-center gap-1">
                                  <span>TYPE</span>
                                  {sortField === 'type' ? (
                                    sortAsc ? <ArrowUp className="w-3 h-3 text-orange-400" /> : <ArrowDown className="w-3 h-3 text-orange-400" />
                                  ) : (
                                    <ArrowUpDown className="w-3 h-3 text-gray-500" />
                                  )}
                                </div>
                              </th>

                              <th
                                onClick={() => handleSortToggle('index_size')}
                                className="py-2.5 px-3 text-right cursor-pointer hover:text-white transition-colors"
                              >
                                <div className="flex items-center justify-end gap-1">
                                  <span>INDEX SIZE</span>
                                  {sortField === 'index_size' ? (
                                    sortAsc ? <ArrowUp className="w-3 h-3 text-orange-400" /> : <ArrowDown className="w-3 h-3 text-orange-400" />
                                  ) : (
                                    <ArrowUpDown className="w-3 h-3 text-gray-500" />
                                  )}
                                </div>
                              </th>
                              <th
                                onClick={() => handleSortToggle('size')}
                                className="py-2.5 px-3 cursor-pointer hover:text-white transition-colors"
                              >
                                <div className="flex items-center gap-1">
                                  <span>TOTAL SIZE</span>
                                  {sortField === 'size' ? (
                                    sortAsc ? <ArrowUp className="w-3 h-3 text-orange-400" /> : <ArrowDown className="w-3 h-3 text-orange-400" />
                                  ) : (
                                    <ArrowUpDown className="w-3 h-3 text-gray-500" />
                                  )}
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-800/60">
                            {filteredObjects.length > 0 ? (
                              filteredObjects.map((item) => {
                                const mb = item.total_size_mb || 0;
                                const pct = Math.min(100, (mb / maxMb) * 100);
                                const badge = getTableTypeBadgeInfo(item.table_type);
                                const ItemIcon = badge.icon;

                                return (
                                  <tr
                                    key={`${item.database}_${item.schema}_${item.table}_${item.table_type}_${typeFilter}`}
                                    onClick={() => handleSelectTable(item)}
                                    className="hover:bg-orange-500/[0.06] cursor-pointer transition-colors group"
                                  >
                                    <td className="py-2.5 px-3 font-semibold text-white group-hover:text-orange-300 font-mono flex items-center gap-2">
                                      <ItemIcon className={`w-3.5 h-3.5 ${badge.text} shrink-0`} />
                                      {item.table}
                                    </td>
                                    <td className="py-2.5 px-3 font-mono text-blue-300 text-[11px]">
                                      {item.schema}
                                    </td>
                                    <td className="py-2.5 px-3">
                                      <span
                                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${badge.bg} ${badge.border} ${badge.text}`}
                                      >
                                        <ItemIcon className="w-2.5 h-2.5" />
                                        {badge.label}
                                      </span>
                                    </td>
                                    <td className="py-2.5 px-3 text-right font-mono text-gray-400 text-[11px]">
                                      {(item.index_size_mb || 0).toFixed(2)} MB
                                    </td>
                                    <td className="py-2.5 px-3">
                                      <div className="flex items-center gap-2.5">
                                        <div className="flex-1 h-2 bg-gray-950 rounded-full overflow-hidden border border-gray-700/80">
                                          <div
                                            className="h-full bg-orange-500 rounded-full shadow-[0_0_8px_rgba(249,115,22,0.6)] transition-all duration-500"
                                            style={{ width: `${Math.max(pct, 2.5).toFixed(1)}%` }}
                                          />
                                        </div>
                                        <span className="text-[10px] font-bold font-mono text-orange-400 bg-gray-900 border border-orange-500/30 px-2 py-0.5 rounded shrink-0 whitespace-nowrap text-right shadow-sm min-w-[68px]">
                                          {mb.toFixed(2)} MB
                                        </span>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan={5} className="py-8 text-center text-xs text-gray-500 italic">
                                  No matching objects found with the selected filter.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* ========================================================================= */}
        {/* VIEW 3: Schema Detail View (with Object Type Segregation Filter Pills) */}
        {/* ========================================================================= */}
        {activeSelection.type === 'schema' && (
          <div className="space-y-4">
            {/* Breadcrumb Trail */}
            <div className="border-b border-gray-700/60 pb-3 space-y-2">
              <div className="text-[11px] text-gray-400 flex items-center gap-1.5 flex-wrap">
                <button
                  onClick={() => {
                    setActiveSelection({ type: 'all' });
                    setTypeFilter('all');
                  }}
                  className="px-2 py-0.5 rounded bg-gray-800/80 border border-gray-700/60 text-gray-300 hover:text-white hover:border-orange-500/40 transition-all font-semibold"
                >
                  All Databases
                </button>
                <span>/</span>
                <button
                  onClick={() => selectDbFromView(activeSelection.db)}
                  className="px-2 py-0.5 rounded bg-gray-800/80 border border-gray-700/60 text-gray-300 hover:text-white hover:border-orange-500/40 transition-all font-semibold font-mono"
                >
                  {activeSelection.db}
                </button>
                <span>/</span>
                <span className="text-white font-semibold font-mono">{activeSelection.schema}</span>
              </div>

              <div className="flex items-center justify-between flex-wrap gap-3 pt-1">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-400 shadow-inner shrink-0">
                    <Folder className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-white font-mono tracking-wide">
                      {activeSelection.schema}
                    </h2>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      Segregated objects in schema{' '}
                      <span className="text-blue-400 font-mono">{activeSelection.schema}</span>. Click a
                      row to inspect columns, definitions, and dependencies.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Object Type Segregation Filter Pills */}
            {(() => {
              const schData = dbHierarchy[activeSelection.db]?.schemas?.[activeSelection.schema];
              const allObjects = schData?.allObjects || [];

              // Calculate counts per type
              const tablesCount = allObjects.filter((o) => getTableTypeCategory(o.table_type) === 'tables').length;
              const viewsCount = allObjects.filter((o) => getTableTypeCategory(o.table_type) === 'views').length;
              const matViewsCount = allObjects.filter((o) => getTableTypeCategory(o.table_type) === 'mat_views').length;
              const sequencesCount = allObjects.filter((o) => getTableTypeCategory(o.table_type) === 'sequences').length;
              const functionsCount = allObjects.filter((o) => getTableTypeCategory(o.table_type) === 'functions').length;
              const proceduresCount = allObjects.filter((o) => getTableTypeCategory(o.table_type) === 'procedures').length;
              const triggersCount = allObjects.filter((o) => getTableTypeCategory(o.table_type) === 'triggers').length;
              const othersCount = allObjects.filter((o) => getTableTypeCategory(o.table_type) === 'others').length;

              const filteredObjects = sortItems(
                allObjects.filter((obj) => {
                  if (!isTypeMatch(obj.table_type, typeFilter)) return false;
                  if (searchLower) {
                    return (
                      obj.table.toLowerCase().includes(searchLower) ||
                      (obj.table_type && obj.table_type.toLowerCase().includes(searchLower))
                    );
                  }
                  return true;
                })
              );

              const maxMb = Math.max(...filteredObjects.map((o) => o.total_size_mb || 0), 0.01);

              return (
                <>
                  <div className="flex items-center gap-2 flex-wrap pb-1">
                    <span className="text-[10px] font-bold uppercase text-gray-400 tracking-wider flex items-center gap-1">
                      <Filter className="w-3 h-3 text-orange-400" /> Filter Type:
                    </span>

                    <button
                      type="button"
                      onClick={() => setTypeFilter('all')}
                      className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all ${
                        typeFilter === 'all'
                          ? 'bg-orange-500 text-white border-orange-400 shadow-md shadow-orange-950/30'
                          : 'bg-gray-850 hover:bg-gray-800 text-gray-300 border-gray-700'
                      }`}
                    >
                      <span>All</span>
                      <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded border ${typeFilter === 'all' ? 'bg-orange-600 border-orange-400 text-white' : 'bg-gray-900 text-gray-300 border-gray-700'}`}>
                        {allObjects.length}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setTypeFilter('tables')}
                      className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all ${
                        typeFilter === 'tables'
                          ? 'bg-emerald-600 text-white border-emerald-400 shadow-md shadow-emerald-950/30'
                          : 'bg-gray-850 hover:bg-gray-800 text-gray-300 border-gray-700'
                      }`}
                    >
                      <Table className={`w-3 h-3 ${typeFilter === 'tables' ? 'text-white' : 'text-emerald-400'}`} />
                      <span>Tables</span>
                      <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded border ${typeFilter === 'tables' ? 'bg-emerald-700 border-emerald-400 text-white' : 'bg-gray-900 text-emerald-300 border-gray-700'}`}>
                        {tablesCount}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setTypeFilter('views')}
                      className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all ${
                        typeFilter === 'views'
                          ? 'bg-cyan-600 text-white border-cyan-400 shadow-md shadow-cyan-950/30'
                          : 'bg-gray-850 hover:bg-gray-800 text-gray-300 border-gray-700'
                      }`}
                    >
                      <Eye className={`w-3 h-3 ${typeFilter === 'views' ? 'text-white' : 'text-cyan-400'}`} />
                      <span>Views</span>
                      <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded border ${typeFilter === 'views' ? 'bg-cyan-700 border-cyan-400 text-white' : 'bg-gray-900 text-cyan-300 border-gray-700'}`}>
                        {viewsCount}
                      </span>
                    </button>

                    {matViewsCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setTypeFilter('mat_views')}
                        className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all ${
                          typeFilter === 'mat_views'
                            ? 'bg-purple-600 text-white border-purple-400 shadow-md shadow-purple-950/30'
                            : 'bg-gray-850 hover:bg-gray-800 text-gray-300 border-gray-700'
                        }`}
                      >
                        <Layers className={`w-3 h-3 ${typeFilter === 'mat_views' ? 'text-white' : 'text-purple-400'}`} />
                        <span>Mat. Views</span>
                        <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded border ${typeFilter === 'mat_views' ? 'bg-purple-700 border-purple-400 text-white' : 'bg-gray-900 text-purple-300 border-gray-700'}`}>
                          {matViewsCount}
                        </span>
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => setTypeFilter('sequences')}
                      className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all ${
                        typeFilter === 'sequences'
                          ? 'bg-amber-600 text-white border-amber-400 shadow-md shadow-amber-950/30'
                          : 'bg-gray-850 hover:bg-gray-800 text-gray-300 border-gray-700'
                      }`}
                    >
                      <Hash className={`w-3 h-3 ${typeFilter === 'sequences' ? 'text-white' : 'text-amber-400'}`} />
                      <span>Sequences</span>
                      <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded border ${typeFilter === 'sequences' ? 'bg-amber-700 border-amber-400 text-white' : 'bg-gray-900 text-amber-300 border-gray-700'}`}>
                        {sequencesCount}
                      </span>
                    </button>

                    {functionsCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setTypeFilter('functions')}
                        className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all ${
                          typeFilter === 'functions'
                            ? 'bg-indigo-600 text-white border-indigo-400 shadow-md shadow-indigo-950/30'
                            : 'bg-gray-850 hover:bg-gray-800 text-gray-300 border-gray-700'
                        }`}
                      >
                        <FileCode2 className={`w-3 h-3 ${typeFilter === 'functions' ? 'text-white' : 'text-indigo-400'}`} />
                        <span>Functions</span>
                        <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded border ${typeFilter === 'functions' ? 'bg-indigo-700 border-indigo-400 text-white' : 'bg-gray-900 text-indigo-300 border-gray-700'}`}>
                          {functionsCount}
                        </span>
                      </button>
                    )}

                    {proceduresCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setTypeFilter('procedures')}
                        className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all ${
                          typeFilter === 'procedures'
                            ? 'bg-violet-600 text-white border-violet-400 shadow-md shadow-violet-950/30'
                            : 'bg-gray-850 hover:bg-gray-800 text-gray-300 border-gray-700'
                        }`}
                      >
                        <Zap className={`w-3 h-3 ${typeFilter === 'procedures' ? 'text-white' : 'text-violet-400'}`} />
                        <span>Procedures</span>
                        <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded border ${typeFilter === 'procedures' ? 'bg-violet-700 border-violet-400 text-white' : 'bg-gray-900 text-violet-300 border-gray-700'}`}>
                          {proceduresCount}
                        </span>
                      </button>
                    )}

                    {triggersCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setTypeFilter('triggers')}
                        className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all ${
                          typeFilter === 'triggers'
                            ? 'bg-rose-600 text-white border-rose-400 shadow-md shadow-rose-950/30'
                            : 'bg-gray-850 hover:bg-gray-800 text-gray-300 border-gray-700'
                        }`}
                      >
                        <Activity className={`w-3 h-3 ${typeFilter === 'triggers' ? 'text-white' : 'text-rose-400'}`} />
                        <span>Triggers</span>
                        <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded border ${typeFilter === 'triggers' ? 'bg-rose-700 border-rose-400 text-white' : 'bg-gray-900 text-rose-300 border-gray-700'}`}>
                          {triggersCount}
                        </span>
                      </button>
                    )}

                    {othersCount > 0 && (
                      <button
                        type="button"
                        onClick={() => setTypeFilter('others')}
                        className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition-all ${
                          typeFilter === 'others'
                            ? 'bg-gray-600 text-white border-gray-400 shadow-md shadow-gray-950/30'
                            : 'bg-gray-850 hover:bg-gray-800 text-gray-300 border-gray-700'
                        }`}
                      >
                        <Layers className={`w-3 h-3 ${typeFilter === 'others' ? 'text-white' : 'text-gray-400'}`} />
                        <span>Others</span>
                        <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded border ${typeFilter === 'others' ? 'bg-gray-700 border-gray-400 text-white' : 'bg-gray-900 text-gray-300 border-gray-700'}`}>
                          {othersCount}
                        </span>
                      </button>
                    )}
                  </div>

                  {/* Objects Table */}
                  <div className="bg-gray-900/80 border border-gray-700/60 rounded-xl shadow-xl overflow-hidden">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="border-b border-gray-700/80 text-[10px] font-bold text-gray-400 uppercase tracking-widest bg-gray-900/90">
                          <th
                            onClick={() => handleSortToggle('name')}
                            className="py-2.5 px-3 cursor-pointer hover:text-white transition-colors"
                          >
                            <div className="flex items-center gap-1">
                              <span>OBJECT NAME</span>
                              {sortField === 'name' ? (
                                sortAsc ? <ArrowUp className="w-3 h-3 text-orange-400" /> : <ArrowDown className="w-3 h-3 text-orange-400" />
                              ) : (
                                <ArrowUpDown className="w-3 h-3 text-gray-500" />
                              )}
                            </div>
                          </th>
                          <th
                            onClick={() => handleSortToggle('type')}
                            className="py-2.5 px-3 cursor-pointer hover:text-white transition-colors"
                          >
                            <div className="flex items-center gap-1">
                              <span>TYPE</span>
                              {sortField === 'type' ? (
                                sortAsc ? <ArrowUp className="w-3 h-3 text-orange-400" /> : <ArrowDown className="w-3 h-3 text-orange-400" />
                              ) : (
                                <ArrowUpDown className="w-3 h-3 text-gray-500" />
                              )}
                            </div>
                          </th>

                          <th
                            onClick={() => handleSortToggle('index_size')}
                            className="py-2.5 px-3 text-right cursor-pointer hover:text-white transition-colors"
                          >
                            <div className="flex items-center justify-end gap-1">
                              <span>INDEX SIZE</span>
                              {sortField === 'index_size' ? (
                                sortAsc ? <ArrowUp className="w-3 h-3 text-orange-400" /> : <ArrowDown className="w-3 h-3 text-orange-400" />
                              ) : (
                                <ArrowUpDown className="w-3 h-3 text-gray-500" />
                              )}
                            </div>
                          </th>
                          <th
                            onClick={() => handleSortToggle('size')}
                            className="py-2.5 px-3 cursor-pointer hover:text-white transition-colors"
                          >
                            <div className="flex items-center gap-1">
                              <span>TOTAL SIZE</span>
                              {sortField === 'size' ? (
                                sortAsc ? <ArrowUp className="w-3 h-3 text-orange-400" /> : <ArrowDown className="w-3 h-3 text-orange-400" />
                              ) : (
                                <ArrowUpDown className="w-3 h-3 text-gray-500" />
                              )}
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800/60">
                        {filteredObjects.length > 0 ? (
                          filteredObjects.map((item) => {
                            const mb = item.total_size_mb || 0;
                            const pct = Math.min(100, (mb / maxMb) * 100);
                            const badge = getTableTypeBadgeInfo(item.table_type);
                            const ItemIcon = badge.icon;

                            return (
                              <tr
                                key={`${item.database}_${item.schema}_${item.table}_${item.table_type}_${typeFilter}`}
                                onClick={() => handleSelectTable(item)}
                                className="hover:bg-orange-500/[0.06] cursor-pointer transition-colors group"
                              >
                                <td className="py-2.5 px-3 font-semibold text-white group-hover:text-orange-300 font-mono flex items-center gap-2">
                                  <ItemIcon className={`w-3.5 h-3.5 ${badge.text} shrink-0`} />
                                  {item.table}
                                </td>
                                <td className="py-2.5 px-3">
                                  <span
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${badge.bg} ${badge.border} ${badge.text}`}
                                  >
                                    <ItemIcon className="w-2.5 h-2.5" />
                                    {badge.label}
                                  </span>
                                </td>
                                <td className="py-2.5 px-3 text-right font-mono text-gray-400 text-[11px]">
                                  {(item.index_size_mb || 0).toFixed(2)} MB
                                </td>
                                <td className="py-2.5 px-3">
                                  <div className="flex items-center gap-2.5">
                                    <div className="flex-1 h-2 bg-gray-950 rounded-full overflow-hidden border border-gray-700/80">
                                      <div
                                        className="h-full bg-orange-500 rounded-full shadow-[0_0_8px_rgba(249,115,22,0.6)] transition-all duration-500"
                                        style={{ width: `${Math.max(pct, 2.5).toFixed(1)}%` }}
                                      />
                                    </div>
                                    <span className="text-[10px] font-bold font-mono text-orange-400 bg-gray-900 border border-orange-500/30 px-2 py-0.5 rounded shrink-0 whitespace-nowrap text-right shadow-sm min-w-[68px]">
                                      {mb.toFixed(2)} MB
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={4} className="py-8 text-center text-xs text-gray-500 italic">
                              No matching objects found with the selected filter.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* ========================================================================= */}
        {/* VIEW 4: Table / Function / Sequence Detail View (/db_monitor_dashboard/schema/table) */}
        {/* ========================================================================= */}
        {activeSelection.type === 'table' && (
          <div className="flex-1 flex flex-col min-h-0">
            {loadingMeta ? (
              <div className="flex flex-col items-center justify-center flex-1 text-gray-400 space-y-2.5 py-16">
                <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                <span className="text-xs font-semibold tracking-wide">
                  Fetching object details & definition...
                </span>
              </div>
            ) : tableMeta ? (
              <div className="space-y-4">
                {/* Header & Breadcrumb Trail */}
                <div className="border-b border-gray-700/60 pb-3 space-y-2">
                  <div className="text-[11px] text-gray-400 flex items-center gap-1.5 flex-wrap">
                    <button
                      onClick={() => {
                        setActiveSelection({ type: 'all' });
                        setTypeFilter('all');
                      }}
                      className="px-2 py-0.5 rounded bg-gray-800/80 border border-gray-700/60 text-gray-300 hover:text-white hover:border-orange-500/40 transition-all font-semibold"
                    >
                      All Databases
                    </button>
                    <span>/</span>
                    <button
                      onClick={() => selectDbFromView(tableMeta.database || activeSelection.item.database)}
                      className="px-2 py-0.5 rounded bg-gray-800/80 border border-gray-700/60 text-gray-300 hover:text-white hover:border-orange-500/40 transition-all font-semibold font-mono"
                    >
                      {tableMeta.database || activeSelection.item.database}
                    </button>
                    <span>/</span>
                    <button
                      onClick={() =>
                        selectSchemaFromView(
                          tableMeta.database || activeSelection.item.database,
                          tableMeta.schema || activeSelection.item.schema
                        )
                      }
                      className="px-2 py-0.5 rounded bg-gray-800/80 border border-gray-700/60 text-gray-300 hover:text-white hover:border-orange-500/40 transition-all font-semibold font-mono"
                    >
                      {tableMeta.schema || activeSelection.item.schema}
                    </button>
                    <span>/</span>
                    <span className="text-white font-semibold font-mono">
                      {tableMeta.name || tableMeta.table || activeSelection.item.table}
                    </span>
                  </div>

                  <div className="flex items-center justify-between flex-wrap gap-3 pt-1">
                    <div className="flex items-center gap-3">
                      {(() => {
                        const badge = getTableTypeBadgeInfo(
                          tableMeta.table_type || activeSelection.item.table_type
                        );
                        const HeaderIcon = badge.icon;
                        return (
                          <>
                            <div className="w-9 h-9 rounded-xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center text-indigo-400 shadow-inner shrink-0">
                              <HeaderIcon className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <h2 className="text-sm font-bold text-white font-mono tracking-wide">
                                  {tableMeta.schema}.{tableMeta.name || tableMeta.table}
                                </h2>
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${badge.bg} ${badge.border} ${badge.text}`}
                                >
                                  <HeaderIcon className="w-2.5 h-2.5" />
                                  {badge.label}
                                </span>

                                {tableMeta.schema_hash && (
                                  <button
                                    onClick={() => copyHashToClipboard(tableMeta.schema_hash!)}
                                    title="Click to copy schema hash"
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-mono text-gray-400 bg-gray-900 border border-gray-750 hover:border-gray-600 hover:text-gray-200 transition-colors"
                                  >
                                    <Binary className="w-2.5 h-2.5 text-orange-400" />
                                    <span>hash: {tableMeta.schema_hash.slice(0, 8)}...</span>
                                    {copiedHash ? (
                                      <Check className="w-2.5 h-2.5 text-emerald-400" />
                                    ) : (
                                      <Copy className="w-2.5 h-2.5 text-gray-500" />
                                    )}
                                  </button>
                                )}
                              </div>
                              {tableMeta.collected_at && (
                                <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1 font-mono">
                                  <Clock className="w-3 h-3 text-gray-500" />
                                  Collected: {formatDate(tableMeta.collected_at)}
                                </p>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </div>

                    <button
                      onClick={() => fetchTableDetails(activeSelection.item)}
                      disabled={loadingMeta}
                      className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-gray-800/80 hover:bg-gray-750 border border-gray-700 text-gray-300 hover:text-white flex items-center gap-1.5 transition-all shadow-sm disabled:opacity-50"
                    >
                      <RefreshCw
                        className={`w-3.5 h-3.5 text-orange-400 ${loadingMeta ? 'animate-spin' : ''}`}
                      />
                      Refresh Details
                    </button>
                  </div>
                </div>

                {/* ------------------------------------------------------------- */}
                {/* CASE A: FUNCTION / PROCEDURE VIEW */}
                {/* ------------------------------------------------------------- */}
                {(getTableTypeCategory(tableMeta.table_type) === 'functions' ||
                  getTableTypeCategory(tableMeta.table_type) === 'procedures' ||
                  tableMeta.definition) && (
                  <div className="space-y-4">
                    {/* Function Meta Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                        <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                          Language
                        </span>
                        <span className="text-sm font-bold text-indigo-400 font-mono mt-0.5 block uppercase">
                          {tableMeta.language || 'plpgsql'}
                        </span>
                      </div>

                      <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                        <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                          Return Type
                        </span>
                        <span className="text-sm font-bold text-emerald-400 font-mono mt-0.5 block">
                          {tableMeta.return_type || 'void'}
                        </span>
                      </div>

                      <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                        <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                          Volatility
                        </span>
                        <span className="text-sm font-bold text-amber-400 font-mono mt-0.5 block uppercase">
                          {tableMeta.volatility || 'VOLATILE'}
                        </span>
                      </div>

                      <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                        <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                          Routine Type
                        </span>
                        <span className="text-sm font-bold text-purple-300 font-mono mt-0.5 block capitalize">
                          {tableMeta.routine_type || 'Function'}
                        </span>
                      </div>

                      <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                        <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                          Security Definer
                        </span>
                        <span
                          className={`text-sm font-bold font-mono mt-0.5 flex items-center gap-1 ${
                            tableMeta.security_definer ? 'text-amber-400' : 'text-gray-400'
                          }`}
                        >
                          {tableMeta.security_definer ? (
                            <>
                              <ShieldAlert className="w-3.5 h-3.5 text-amber-400" /> YES
                            </>
                          ) : (
                            <>
                              <ShieldCheck className="w-3.5 h-3.5 text-gray-500" /> NO
                            </>
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Arguments Section */}
                    {tableMeta.arguments && (
                      <div className="bg-gray-900/80 border border-gray-700/60 rounded-xl p-3 shadow-md space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-bold text-cyan-400 uppercase tracking-wider flex items-center gap-1.5">
                            <Code2 className="w-3.5 h-3.5 text-cyan-400" /> Arguments & Signature
                          </span>
                        </div>
                        <div className="bg-gray-950 p-2.5 rounded-lg border border-gray-800 font-mono text-xs text-cyan-300 break-words">
                          ({tableMeta.arguments})
                        </div>
                      </div>
                    )}

                    {/* Source Code Definition */}
                    {tableMeta.definition ? (
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] font-bold text-orange-400 uppercase tracking-wider flex items-center gap-1.5">
                            <FileCode2 className="w-3.5 h-3.5 text-orange-400" /> Source Definition
                          </span>
                          <button
                            onClick={() => copyDefinitionToClipboard(tableMeta.definition!)}
                            className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 hover:text-white flex items-center gap-1.5 transition-all shadow-sm"
                          >
                            {copiedDef ? (
                              <>
                                <Check className="w-3 h-3 text-emerald-400" /> Copied!
                              </>
                            ) : (
                              <>
                                <Copy className="w-3 h-3 text-gray-400" /> Copy SQL
                              </>
                            )}
                          </button>
                        </div>
                        <div className="bg-gray-950 border border-gray-800 rounded-xl p-3.5 overflow-x-auto custom-scroll shadow-inner">
                          <pre className="font-mono text-xs text-gray-200 leading-relaxed">
                            <code>{tableMeta.definition}</code>
                          </pre>
                        </div>
                      </div>
                    ) : (
                      <div className="py-8 text-center text-xs text-gray-500 italic">
                        No function definition body returned.
                      </div>
                    )}
                  </div>
                )}

                {/* ------------------------------------------------------------- */}
                {/* CASE B: SEQUENCE VIEW */}
                {/* ------------------------------------------------------------- */}
                {getTableTypeCategory(tableMeta.table_type) === 'sequences' && (
                  <div className="space-y-4">
                    {/* Sequence Meta Cards Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                      <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                        <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                          Data Type
                        </span>
                        <span className="text-sm font-bold text-amber-400 font-mono mt-0.5 block">
                          {tableMeta.data_type || 'bigint'}
                        </span>
                      </div>

                      <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                        <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                          Start Value
                        </span>
                        <span className="text-sm font-bold text-white font-mono mt-0.5 block">
                          {String(tableMeta.start_value ?? 1)}
                        </span>
                      </div>

                      <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                        <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                          Increment By
                        </span>
                        <span className="text-sm font-bold text-emerald-400 font-mono mt-0.5 block">
                          {String(tableMeta.increment_by ?? 1)}
                        </span>
                      </div>

                      <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                        <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                          Min Value
                        </span>
                        <span className="text-sm font-bold text-gray-300 font-mono mt-0.5 block">
                          {String(tableMeta.min_value ?? 1)}
                        </span>
                      </div>

                      <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md truncate">
                        <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                          Max Value
                        </span>
                        <span
                          className="text-xs font-bold text-gray-300 font-mono mt-0.5 block truncate"
                          title={String(tableMeta.max_value)}
                        >
                          {String(tableMeta.max_value ?? '9223372036854775807')}
                        </span>
                      </div>

                      <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                        <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                          Cache Size
                        </span>
                        <span className="text-sm font-bold text-cyan-400 font-mono mt-0.5 block">
                          {String(tableMeta.cache_size ?? 1)}
                        </span>
                      </div>

                      <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                        <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                          Is Cycled
                        </span>
                        <span
                          className={`text-sm font-bold font-mono mt-0.5 block ${
                            tableMeta.is_cycled ? 'text-emerald-400' : 'text-gray-400'
                          }`}
                        >
                          {tableMeta.is_cycled ? 'YES' : 'NO'}
                        </span>
                      </div>
                    </div>

                    {/* Sequence Ownership & Dependencies */}
                    {tableMeta.owned_by_table && (
                      <div className="bg-gray-900/80 border border-gray-700/60 rounded-xl p-3 shadow-md space-y-2">
                        <span className="text-[11px] font-bold text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                          <GitCommit className="w-3.5 h-3.5 text-amber-400" /> Sequence Dependency & Ownership
                        </span>
                        <div className="bg-gray-950 p-3 rounded-lg border border-gray-800 flex items-center justify-between flex-wrap gap-2">
                          <div className="flex items-center gap-2 text-xs font-mono">
                            <span className="text-gray-400">Attached Column:</span>
                            <span className="text-blue-400 font-semibold">
                              {tableMeta.owned_by_schema || tableMeta.schema}.{tableMeta.owned_by_table}
                            </span>
                            <span className="text-gray-500">→</span>
                            <span className="text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/30">
                              {tableMeta.owned_by_column}
                            </span>
                          </div>

                          <button
                            onClick={() =>
                              handleSelectTable({
                                database: tableMeta.database,
                                schema: tableMeta.owned_by_schema || tableMeta.schema,
                                table: tableMeta.owned_by_table!,
                                table_type: 'table',
                              })
                            }
                            className="px-2.5 py-1 rounded text-xs font-semibold bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 border border-blue-500/40 flex items-center gap-1 transition-all"
                          >
                            <span>Inspect Table</span>
                            <ArrowRight className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ------------------------------------------------------------- */}
                {/* CASE C: STANDARD TABLE / VIEW / MAT. VIEW */}
                {/* ------------------------------------------------------------- */}
                {getTableTypeCategory(tableMeta.table_type) !== 'functions' &&
                  getTableTypeCategory(tableMeta.table_type) !== 'procedures' &&
                  getTableTypeCategory(tableMeta.table_type) !== 'sequences' &&
                  !tableMeta.definition && (
                    <div className="space-y-4">
                      {/* Metadata Cards Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                          <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                            Total Size
                          </span>
                          <span className="text-sm font-bold text-orange-400 font-mono mt-0.5 block">
                            {(tableMeta.total_size_mb || 0).toFixed(2)} MB
                          </span>
                        </div>

                        <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                          <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                            Index Size
                          </span>
                          <span className="text-sm font-bold text-purple-300 font-mono mt-0.5 block">
                            {(tableMeta.index_size_mb || 0).toFixed(2)} MB
                          </span>
                        </div>

                        <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                          <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                            Columns
                          </span>
                          <span className="text-sm font-bold text-cyan-400 font-mono mt-0.5 block">
                            {tableMeta.columns ? tableMeta.columns.length : 0} Columns
                          </span>
                        </div>

                        <div className="bg-gray-900/90 border border-gray-800 rounded-xl p-2.5 shadow-md">
                          <span className="text-[9px] text-gray-400 uppercase font-bold tracking-wider block">
                            Indexes
                          </span>
                          <span className="text-sm font-bold text-blue-400 font-mono mt-0.5 block">
                            {tableMeta.indexes ? tableMeta.indexes.length : 0} Indexes
                          </span>
                        </div>
                      </div>

                      {/* Columns Table Section */}
                      <div className="space-y-2">
                        <h3 className="text-[11px] font-bold text-orange-400 uppercase tracking-widest flex items-center gap-1.5">
                          <Sparkles className="w-3.5 h-3.5 text-orange-400" />
                          Columns ({tableMeta.columns ? tableMeta.columns.length : 0})
                        </h3>
                        <div className="bg-gray-900/80 border border-gray-700/60 rounded-xl shadow-xl overflow-x-auto custom-scroll">
                          <table className="w-full text-left text-xs border-collapse">
                            <thead>
                              <tr className="border-b border-gray-700/80 bg-gray-900/90 text-[10px] text-gray-400 uppercase font-bold tracking-widest">
                                <th className="py-2.5 px-3 text-center w-12">#</th>
                                <th className="py-2.5 px-3">COLUMN NAME</th>
                                <th className="py-2.5 px-3">DATA TYPE</th>
                                <th className="py-2.5 px-3">DEFAULT</th>
                                <th className="py-2.5 px-3 text-center">NULLABLE</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800/60">
                              {tableMeta.columns && tableMeta.columns.length > 0 ? (
                                tableMeta.columns
                                  .slice()
                                  .sort(
                                    (a, b) =>
                                      (a.ordinal_position || 0) - (b.ordinal_position || 0)
                                  )
                                  .map((col, i) => (
                                    <tr
                                      key={col.name || i}
                                      className="hover:bg-gray-800/50 transition-colors"
                                    >
                                      <td className="py-2.5 px-3 text-center font-mono text-[10px] text-gray-500">
                                        {col.ordinal_position ?? i + 1}
                                      </td>
                                      <td className="py-2.5 px-3 font-semibold text-white flex items-center gap-1.5 font-mono">
                                        {col.name}
                                        {(col.is_primary || col.is_primary_key) && (
                                          <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold bg-orange-500/20 text-orange-400 border border-orange-500/40 shadow-[0_0_8px_rgba(249,115,22,0.2)] flex items-center gap-0.5 shrink-0">
                                            <Key className="w-2.5 h-2.5" /> PK
                                          </span>
                                        )}
                                      </td>
                                      <td className="py-2.5 px-3">
                                        <span className="font-mono text-[11px] text-purple-300 bg-purple-500/10 px-1.5 py-0.5 rounded border border-purple-500/20">
                                          {col.data_type || '-'}
                                        </span>
                                      </td>
                                      <td className="py-2.5 px-3 font-mono text-gray-400">
                                        {col.default !== null && col.default !== undefined ? (
                                          <code className="text-gray-300 bg-gray-950 px-1.5 py-0.5 rounded border border-gray-800 text-[11px]">
                                            {String(col.default)}
                                          </code>
                                        ) : (
                                          '-'
                                        )}
                                      </td>
                                      <td className="py-2.5 px-3 text-center">
                                        {col.is_nullable || col.nullable ? (
                                          <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold text-gray-400 bg-gray-800/80 border border-gray-700">
                                            YES
                                          </span>
                                        ) : (
                                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold text-rose-400 bg-rose-500/15 border border-rose-500/30">
                                            NO
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  ))
                              ) : (
                                <tr>
                                  <td
                                    colSpan={5}
                                    className="py-6 text-center text-xs text-gray-500 italic"
                                  >
                                    No columns metadata found.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Indexes Table Section */}
                      {tableMeta.indexes && tableMeta.indexes.length > 0 && (
                        <div className="space-y-2">
                          <h3 className="text-[11px] font-bold text-blue-400 uppercase tracking-widest flex items-center gap-1.5">
                            <Key className="w-3.5 h-3.5 text-blue-400" />
                            Indexes ({tableMeta.indexes.length})
                          </h3>
                          <div className="bg-gray-900/80 border border-gray-700/60 rounded-xl shadow-xl overflow-hidden">
                            <table className="w-full text-left text-xs border-collapse">
                              <thead>
                                <tr className="border-b border-gray-700/80 bg-gray-900/90 text-[10px] text-gray-400 uppercase font-bold tracking-widest">
                                  <th className="py-2 px-3">INDEX NAME</th>
                                  <th className="py-2 px-3">COLUMNS</th>
                                  <th className="py-2 px-3 text-center">UNIQUE</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-800/60">
                                {tableMeta.indexes.map((idx, i) => {
                                  const cols = Array.isArray(idx.columns)
                                    ? idx.columns.join(', ')
                                    : String(idx.columns || '');
                                  return (
                                    <tr key={i} className="hover:bg-gray-800/50 transition-colors">
                                      <td className="py-2 px-3 font-semibold text-white font-mono flex items-center gap-1.5">
                                        {idx.name}
                                        {idx.is_primary && (
                                          <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold bg-orange-500/20 text-orange-400 border border-orange-500/40 shrink-0">
                                            PK
                                          </span>
                                        )}
                                      </td>
                                      <td className="py-2 px-3 font-mono text-blue-300 text-[11px]">
                                        {cols || '-'}
                                      </td>
                                      <td className="py-2 px-3 text-center">
                                        {idx.is_unique ? (
                                          <span className="inline-flex items-center gap-1 text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-1.5 py-0.5 rounded font-bold text-[9px]">
                                            <CheckCircle2 className="w-3 h-3" /> UNIQUE
                                          </span>
                                        ) : (
                                          <span className="text-gray-500 text-[9px] font-semibold">
                                            NO
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Foreign Keys Table Section */}
                      {tableMeta.foreign_keys && tableMeta.foreign_keys.length > 0 && (
                        <div className="space-y-2">
                          <h3 className="text-[11px] font-bold text-emerald-400 uppercase tracking-widest flex items-center gap-1.5">
                            <ArrowRight className="w-3.5 h-3.5 text-emerald-400" />
                            Foreign Keys ({tableMeta.foreign_keys.length})
                          </h3>
                          <div className="bg-gray-900/80 border border-gray-700/60 rounded-xl shadow-xl overflow-hidden">
                            <table className="w-full text-left text-xs border-collapse">
                              <thead>
                                <tr className="border-b border-gray-700/80 bg-gray-900/90 text-[10px] text-gray-400 uppercase font-bold tracking-widest">
                                  <th className="py-2 px-3">CONSTRAINT NAME</th>
                                  <th className="py-2 px-3">COLUMN</th>
                                  <th className="py-2 px-3">FOREIGN REFERENCE</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-800/60">
                                {tableMeta.foreign_keys.map((fk, i) => (
                                  <tr key={i} className="hover:bg-gray-800/50 transition-colors">
                                    <td className="py-2 px-3 font-mono text-gray-400 text-[11px]">
                                      {fk.name}
                                    </td>
                                    <td className="py-2 px-3 font-semibold text-white font-mono">
                                      {fk.column}
                                    </td>
                                    <td className="py-2 px-3 font-mono text-emerald-400 flex items-center gap-1.5 text-[11px]">
                                      <ArrowRight className="w-3 h-3 text-gray-500 shrink-0" />
                                      <span className="bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded text-emerald-300">
                                        {fk.ref_schema || fk.foreign_table_schema}.
                                        {fk.ref_table || fk.foreign_table_name}(
                                        {fk.ref_column || fk.foreign_column_name})
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center flex-1 text-gray-500 space-y-2 py-16">
                <Table className="w-12 h-12 text-gray-600" />
                <p className="text-xs">
                  Select an object from the tree on the left to inspect its columns, data types, indexes, and relations.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SchemaView;
